import { isInFlightStatus, isActiveProcessingStatus, isFailedStatus } from "@/constants/documentStatus";
import { AlertType, DocumentRead, DocumentStatus, ResourceName } from "@/types";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { useCallback, useEffect, useRef } from "react";
import { useGetOne, useNotify, useRefresh } from "react-admin";

/**
 * Poll cadence is a ladder keyed off how long the document has sat in its
 * current status. Transitions cluster early (a worker picks the file up within
 * seconds; extraction of a typical file finishes well inside a minute), so the
 * first 30 s are checked tightly, then the cadence relaxes. Any status change
 * resets the ladder to its first rung.
 *
 * Active (PROCESSING, LOADING) vs waiting (UPLOADED, REPROCESS, APPROVED,
 * LOADED) rungs differ because a queued document is waiting on a worker, not on
 * work, and a slower check costs the user less there.
 */
export const ACTIVE_POLL_LADDER_MS = [2_000, 4_000, 8_000] as const;
export const WAITING_POLL_LADDER_MS = [3_000, 6_000, 15_000] as const;
/** Elapsed-since-transition thresholds that move the ladder to the next rung. */
export const POLL_LADDER_STEPS_MS = [30_000, 120_000] as const;

/**
 * Interval for a status after `elapsedMs` in it. `false` for terminal statuses
 * (REVIEW_REQUIRED, COMPLETED, REJECTED, the failures) — nothing will move them
 * without a user. Exported for tests and for the indicator's "checks every Ns".
 */
export const resolvePollInterval = (status: DocumentStatus | undefined, elapsedMs: number): number | false => {
  if (!isInFlightStatus(status)) return false;
  const ladder = isActiveProcessingStatus(status) ? ACTIVE_POLL_LADDER_MS : WAITING_POLL_LADDER_MS;
  const rung = POLL_LADDER_STEPS_MS.filter((threshold) => elapsedMs >= threshold).length;
  return ladder[Math.min(rung, ladder.length - 1)];
};

export interface DocumentStatusPollingResult {
  /** Current interval, or false when the document is terminal. */
  pollIntervalMs: number | false;
  /** Refetch the document and everything derived from it right now. */
  checkNow: () => void;
}

/**
 * Polls a single document while the backend may still move it, refreshes the
 * page on every status change, and toasts when it lands in a failed state.
 *
 * - Cadence: see {@link resolvePollInterval}. Evaluated after every fetch, so a
 *   status change observed on one tick re-plans the next one.
 * - Transition: `refresh()` invalidates every query on the page (loads,
 *   transformations, the record itself) the moment a new status is seen.
 * - Focus: coming back to the tab refetches immediately rather than waiting out
 *   the current interval.
 *
 * @param documentId - The ID of the document to poll
 * @param currentStatus - The status the page currently shows
 */
export const useDocumentStatusPolling = (
  documentId?: string,
  currentStatus?: DocumentStatus
): DocumentStatusPollingResult => {
  const refresh = useRefresh();
  const notify = useNotify();
  const previousStatusRef = useRef<DocumentStatus | null>(null);
  // When the current status was first observed — the ladder's clock.
  const statusSinceRef = useRef<number>(Date.now());

  const shouldPoll = isInFlightStatus(currentStatus);

  const { data } = useGetOne<DocumentRead>(
    ResourceName.DOCUMENTS,
    { id: documentId },
    {
      refetchInterval: (query) => {
        const observed = (query.state.data as DocumentRead | undefined)?.status ?? currentStatus;
        return resolvePollInterval(observed, Date.now() - statusSinceRef.current);
      },
      refetchOnWindowFocus: true,
      enabled: shouldPoll && !!documentId,
    }
  );

  useEffect(() => {
    if (!data?.status) return;
    if (data.status === previousStatusRef.current) return;

    const prevStatus = previousStatusRef.current;
    previousStatusRef.current = data.status;
    statusSinceRef.current = Date.now();

    // Skip notification on initial mount (prevStatus is null)
    if (prevStatus === null) return;

    console.log(`Document status changed: ${prevStatus} -> ${data.status}`);

    // Notify user if the document transitioned to a failed state
    if (isFailedStatus(data.status)) {
      const statusLabel = formatToTitleCase(data.status);
      notify(`Document status: ${statusLabel}`, { type: AlertType.ERROR });
    }

    refresh();
  }, [data?.status, refresh, notify]);

  const checkNow = useCallback(() => refresh(), [refresh]);

  return {
    pollIntervalMs: resolvePollInterval(currentStatus, Date.now() - statusSinceRef.current),
    checkNow,
  };
};
